import {
  type ElementType,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type RefAttributes,
  createContext,
  forwardRef,
  useContext,
} from "react";
import { cx } from "@zenchef/styled-system/css";
import {
  type StyledComponent,
  isCssProperty,
  styled,
} from "@zenchef/styled-system/jsx";

type Props = Record<string, unknown>;
interface Recipe {
  (props?: Props): Props;
  splitVariantProps: (props: Props) => [Props, Props];
}
export type Slot<R extends Recipe> = keyof ReturnType<R>;
interface Options {
  forwardProps?: string[];
}

const shouldForwardProp = (
  prop: string,
  variantKeys: string[],
  options: Options = {},
) =>
  options.forwardProps?.includes(prop) ??
  (!variantKeys.includes(prop) && !isCssProperty(prop));

/**
 * Create a context for styled components. (generated by @park-ui/cli command)
 * @param recipe - The recipe to use to generate the styles.
 * @returns A set of functions to create a styled component with a context.
 */
export const createStyleContext = <R extends Recipe>(recipe: R) => {
  const StyleContext = createContext<Record<Slot<R>, string> | null>(null);

  const withRootProvider = <P extends Record<string, never>>(
    Component: ElementType,
  ) => {
    const StyledComponent = (props: P) => {
      const [variantProps, otherProps] = recipe.splitVariantProps(props);
      const slotStyles = recipe(variantProps) as Record<Slot<R>, string>;

      return (
        <StyleContext.Provider value={slotStyles}>
          <Component {...otherProps} />
        </StyleContext.Provider>
      );
    };
    return StyledComponent;
  };

  const withProvider = <T, P extends { className?: string | undefined }>(
    Component: ElementType,
    slot: Slot<R>,
    options?: Options,
  ): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> => {
    const StyledComponent = styled(
      Component,
      {},
      {
        shouldForwardProp: (prop, variantKeys) =>
          shouldForwardProp(prop, variantKeys, options),
      },
    ) as StyledComponent<ElementType>;
    const StyledSlotProvider = forwardRef<T, P>((props, ref) => {
      const [variantProps, otherProps] = recipe.splitVariantProps(props);
      const slotStyles = recipe(variantProps) as Record<Slot<R>, string>;

      return (
        <StyleContext.Provider value={slotStyles}>
          <StyledComponent
            {...otherProps}
            ref={ref}
            className={cx(slotStyles?.[slot], props.className)}
          />
        </StyleContext.Provider>
      );
    });
    // @ts-expect-error: is generated by @park-ui/cli command
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    StyledSlotProvider.displayName = Component.displayName || Component.name;

    return StyledSlotProvider;
  };

  const withContext = <T, P extends { className?: string | undefined }>(
    Component: ElementType,
    slot: Slot<R>,
  ): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> => {
    const StyledComponent = styled(Component);
    const StyledSlotComponent = forwardRef<T, P>((props, ref) => {
      const slotStyles = useContext(StyleContext);
      return (
        <StyledComponent
          {...props}
          ref={ref}
          className={cx(slotStyles?.[slot], props.className)}
        />
      );
    });
    // @ts-expect-error: is generated by @park-ui/cli command
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    StyledSlotComponent.displayName = Component.displayName || Component.name;

    return StyledSlotComponent;
  };

  return {
    withRootProvider,
    withProvider,
    withContext,
  };
};
